import { ActionIcon, Switch, Text, Tooltip } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { DataTableColumn } from 'mantine-datatable';
import React from 'react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { TableMode } from 'src/common/components/table/hooks/useTable';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { formatDate } from 'src/financial/financial-data/tables/FinancialItemGenericTable';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import { DataUploadFileContentModal } from './DataUploadFileContentModal';
import { AdminDataUploadFile } from './types';

type DataUploadFilesTableProps = {
  company: Company;
};

export function DataUploadFilesTable({ company }: DataUploadFilesTableProps) {
  const [selectedFileId, setSelectedFileId] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<TableMode>('server');
  const { createLink, isLoading: isDownloading } = useFileTemporaryLinkMutation();

  const handleDownload = async (fileId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const link = await createLink(fileId);
      window.open(link.url, '_blank');
    } catch (err) {
      notifyAndLogError(
        'Failed to download file',
        'An error occurred while trying to download the file. Please try again later.',
        err,
        { fileId },
      );
    }
  };

  const columns: DataTableColumn<AdminDataUploadFile>[] = [
    {
      accessor: 'id',
      title: 'ID',
      render: (record: AdminDataUploadFile) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.id}
        </Text>
      ),
    },
    {
      accessor: 'fileId',
      title: 'File ID',
      width: 80,
      render: (record: AdminDataUploadFile) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.fileId}
        </Text>
      ),
    },
    {
      accessor: 'fileName',
      title: 'File Name',
      render: (record: AdminDataUploadFile) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.fileName ?? '-'}
        </Text>
      ),
    },
    {
      accessor: 'clientName',
      title: 'Client',
      render: (record: AdminDataUploadFile) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.clientName ?? '-'}
        </Text>
      ),
    },
    {
      accessor: 'hasParsedContent',
      title: 'Has Parsed',
      width: 130,
      render: (record: AdminDataUploadFile) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {String(record.hasParsedContent)}
        </Text>
      ),
    },
    {
      accessor: 'hasWrangledContent',
      title: 'Has Wrangled',
      width: 130,
      render: (record: AdminDataUploadFile) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {String(record.hasWrangledContent)}
        </Text>
      ),
    },
    {
      accessor: 'hasCachedDetection',
      title: 'Has Detections',
      width: 130,
      render: (record: AdminDataUploadFile) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {String(record.hasCachedDetection)}
        </Text>
      ),
    },
    {
      accessor: 'isDetectionComplete',
      title: 'Detection Done',
      width: 130,
      render: (record: AdminDataUploadFile) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {String(record.isDetectionComplete)}
        </Text>
      ),
    },
    {
      accessor: 'documentDate',
      title: 'Document Date',
      width: 120,
      render: (record: AdminDataUploadFile) => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.documentDate ? formatDate(record.documentDate) : 'No date'}
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      width: 50,
      render: (record: AdminDataUploadFile) => (
        <Tooltip label="Download file">
          <ActionIcon
            variant="subtle"
            onClick={e => handleDownload(record.fileId, e)}
            loading={isDownloading}
          >
            <IconDownload size={16} />
          </ActionIcon>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <DataUploadFileContentModal
        fileId={selectedFileId}
        companyId={company.id}
        onClose={() => setSelectedFileId(null)}
      />

      <SimpleTable<AdminDataUploadFile>
        key={mode}
        mode={mode}
        resource={`admin/companies/${company.id}/data-upload-files`}
        title="Data Upload Files"
        columns={columns}
        onRowClick={(record: AdminDataUploadFile) => setSelectedFileId(record.id)}
        showCount
        HeaderRow={
          <Switch
            label="Enable search (loads all data, so may be very slow)"
            checked={mode === 'client'}
            onChange={e => setMode(e.currentTarget.checked ? 'client' : 'server')}
          />
        }
      />
    </>
  );
}
